// <s-order-wizard> turns the contact form's fieldsets into short steps with a progress bar.
// Without JavaScript (and in the theme editor) every step stays visible, so the form always works.
class SOrderWizard extends HTMLElement {
  #ac;
  #steps = [];
  #index = 0;

  connectedCallback() {
    if (this.hasAttribute('data-static')) return;
    this.#steps = [...this.querySelectorAll('[data-step]')];
    if (this.#steps.length < 2) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.setAttribute('data-enhanced', '');
    this.addEventListener('command', (event) => {
      if (event.command === '--next') this.#next();
      if (event.command === '--back') this.#go(this.#index - 1);
    }, opts);

    // Single-choice steps advance on their own so the order takes seconds, not minutes.
    this.addEventListener('change', (event) => {
      const step = this.#steps[this.#index];
      if (!step?.hasAttribute('data-auto-advance')) return;
      if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
        window.setTimeout(() => this.#next(), 320);
      }
    }, opts);

    // Package buttons elsewhere on the page preselect the package here.
    document.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-package]') : null;
      if (!trigger) return;
      const input = this.querySelector(`input[data-package-option="${CSS.escape(trigger.dataset.package)}"]`);
      if (input) input.checked = true;
    }, opts);

    this.#go(0, false);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #next() {
    const step = this.#steps[this.#index];
    const fields = [...step.querySelectorAll('input, textarea, select')];
    const invalid = fields.find((field) => !field.checkValidity());
    if (invalid) {
      invalid.reportValidity();
      return;
    }
    this.#go(this.#index + 1);
  }

  #go(index, focus = true) {
    const total = this.#steps.length;
    this.#index = Math.max(0, Math.min(index, total - 1));

    this.#steps.forEach((step, i) => {
      step.hidden = i !== this.#index;
    });

    this.querySelectorAll('[data-progress-item]').forEach((item, i) => {
      item.dataset.state = i < this.#index ? 'done' : i === this.#index ? 'current' : 'upcoming';
      if (i === this.#index) item.setAttribute('aria-current', 'step');
      else item.removeAttribute('aria-current');
    });

    this.style.setProperty('--progress', `${(this.#index / (total - 1)) * 100}%`);

    const back = this.querySelector('[data-wizard-back]');
    const next = this.querySelector('[data-wizard-next]');
    const submit = this.querySelector('[data-wizard-submit]');
    if (back) back.hidden = this.#index === 0;
    if (next) next.hidden = this.#index === total - 1;
    if (submit) submit.hidden = this.#index !== total - 1;

    if (focus) {
      const heading = this.#steps[this.#index].querySelector('legend');
      heading?.setAttribute('tabindex', '-1');
      heading?.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('s-order-wizard')) customElements.define('s-order-wizard', SOrderWizard);
